import { Injectable } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import compact from 'lodash-es/compact';
import each from 'lodash-es/each';
import first from 'lodash-es/first';
import flattenDeep from 'lodash-es/flattenDeep';
import sortBy from 'lodash-es/sortBy';
import take from 'lodash-es/take';
import { IPioEngineRequest, IPioEngineRequestField, IPioEngineResult, IPioLoadOptions } from './interfaces';
import { PioService } from './pio.service';

@Injectable({
    providedIn: 'root'
})
export class PioRecommendService {
    get enabled(): boolean {
        return this.pioService.enabled;
    }

    constructor(
        private requestService: RequestService,
        private app: App,
        private pioService: PioService
    ) {}

    load(status: Status | undefined, requestData: IPioLoadOptions): void | Promise<{ products: number[] }> {
        const engine = requestData.engine || 'universal';

        const request: any = {};
        if (requestData.limit) {
            request.num = requestData.limit;
        }
        if (requestData.request) {
            Object.assign(request, requestData.request);
        }

        switch (requestData.engine) {
            case 'ecommerce':
                this.bindItem(requestData, request);
                if (requestData.user) {
                    this.bindUser(requestData, request);
                }
                break;
            case 'complementary':
                this.bindItems(requestData, request);
                this.bindBlacklist('blacklist', requestData, request);
                break;
            case 'similar':
                this.bindItems(requestData, request);
                this.bindCategories(requestData, request, true);
                break;
            case 'universal':
            default:
                this.bindItem(requestData, request);
                this.bindBlacklist('blacklistItems', requestData, request);
                this.bindCategories(requestData, request);
                this.bindAvailbilityFilter(request);
                this.bindBoost(requestData, request);
                if (requestData.user) {
                    this.bindUser(requestData, request);
                }
                break;
        }

        return this.app.cache.cacheRequest<{ products: number[] }>(['pio.recommend', request], () =>
            this.requestService.send<IPioEngineResult>(
                status,
                {
                    path:
                        'pioload/' +
                        engine
                            .replace(/https?:\/\//, '')
                            .replace(/\//g, '')
                            .replace(':', '/'),
                    data: request
                },
                result => {
                    return {
                        products:
                            engine === 'complementary'
                                ? this.normalizeComplementaryResult(result, request.num)
                                : result.itemScores
                                      .map(item => this.pioService.toProductId(item.item))
                                      .filter(i => i.store === this.app.multisite.storeId)
                                      .map(i => i.id)
                    };
                },
                undefined,
                { api: 'node' }
            )
        );
    }

    /**
     * Binds item from request
     * @param {IPioLoadOptions}   requestData
     * @param {IPioEngineRequest} target
     */
    protected bindItem(requestData: IPioLoadOptions, target: IPioEngineRequest) {
        const productId = Array.isArray(requestData.products) ? first(requestData.products) : requestData.products;
        if (productId) {
            target.item = this.pioService.toItemId(productId);
        }
    }

    /**
     * Binds items from request
     * @param {IPioLoadOptions}   requestData
     * @param {IPioEngineRequest} target
     */
    protected bindItems(requestData: IPioLoadOptions, target: IPioEngineRequest) {
        if (requestData.products) {
            target.items = Array.isArray(requestData.products)
                ? requestData.products.map(i => this.pioService.toItemId(i))
                : this.pioService.toItemId(requestData.products);
        }
    }

    /**
     * Binds blacklisted items from request
     * @param {string}   attrName Atribute name to bind to
     * @param {IPioLoadOptions}   requestData
     * @param {IPioEngineRequest} target
     */
    protected bindBlacklist(attrName: string, requestData: IPioLoadOptions, target: IPioEngineRequest) {
        const blacklist = compact(flattenDeep<string | number>(requestData.blacklist || [])).map(i => this.pioService.toItemId(i));
        if (blacklist && blacklist.length) {
            target[attrName] = blacklist;
        }
    }

    /**
     * Binds categories filter from request
     * @param {string}   attrName Atribute name to bind to
     * @param {IPioLoadOptions}   requestData
     * @param {IPioEngineRequest} target
     */
    protected bindCategories(requestData: IPioLoadOptions, target: IPioEngineRequest, similarEngineSyntax: boolean = false) {
        const categories = compact(Array.isArray(requestData.category) ? requestData.category : [requestData.category]).map(i =>
            (<number>i).toString()
        );
        if (categories.length) {
            if (similarEngineSyntax) {
                target.categories = categories;
            } else {
                this.addField(target, {
                    name: 'category',
                    values: categories,
                    bias: -1 // filter out all except the above
                });
            }
        }
    }

    /**
     * Binds availbility filter (in stock, routable)
     * @param {string}   attrName Atribute name to bind to
     * @param {IPioLoadOptions}   requestData
     * @param {IPioEngineRequest} target
     */
    protected bindAvailbilityFilter(target: IPioEngineRequest) {
        this.addField(target, {
            name: 'available',
            values: ['1'],
            bias: -1 // filter out all except the above
        });
    }

    protected bindUser(requestData: IPioLoadOptions, target: IPioEngineRequest) {
        const userId = this.pioService.getCookieUserId();
        if (userId) {
            target.user = userId;
        }
        // if user id does not exists then user has no cookie (so its fresh user and results will be same without user param)
    }

    /**
     * Binds boost params
     * @param {string}   attrName Atribute name to bind to
     * @param {IPioLoadOptions}   requestData
     * @param {IPioEngineRequest} target
     */
    protected bindBoost(requestData: IPioLoadOptions, target: IPioEngineRequest) {
        if (requestData.boost) {
            if (Array.isArray(requestData.boost)) {
                requestData.boost.forEach(field => this.addField(target, field));
            } else {
                this.addField(target, requestData.boost);
            }
        }
    }

    /**
     * Pushes field to target
     * @param {IPioEngineRequest}       target
     * @param {IPioEngineRequestField} field
     */
    protected addField(target: IPioEngineRequest, field: IPioEngineRequestField) {
        if (!target.fields) {
            target.fields = [];
        }
        target.fields.push(field);
    }

    /**
     * Results comming from PIO come as array of rules : [ {cond : string, itemScores: []}]
     * We will flatten that but in a smart way.
     *
     * First wi will prapare map
     * 1. we will index positions in array as rank (the lowest the better match it is)
     * 2. we will index qties (the more the better match it is)
     *
     * Then we will sort by special rule
     * -1000 * item.qty + item.rank;
     *
     * and pick only requried number of items
     *
     * @param  {result} Result data
     * @param  {number} num Max results
     * @return {number[]} array with product ids sorted by rank
     */
    protected normalizeComplementaryResult(result, num: number) {
        const resultMap: { [key: string]: { id: string; qty: number; rank: number } } = {};
        each(result.rules, function (rule: any) {
            each(rule.itemScores, function (score: any, index: string) {
                const id = score.item;
                const min = Math.min;
                const rank = Number(index);
                if (resultMap[id]) {
                    resultMap[id].qty++;
                    resultMap[id].rank = min(resultMap[id].rank, rank);
                } else {
                    resultMap[id] = {
                        id: this.pioService.toProductId(id),
                        qty: 1,
                        rank
                    };
                }
            });
        });
        return take(
            sortBy(Object.values(resultMap), item => -1000 * item.qty + item.rank).map(i => i.id),
            num
        );
    }
}
